import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { validateImage } from 'image-validator';
import { QuillEditorComponent } from 'ngx-quill';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClubsStore } from '../../../../clubs/clubs.store';
import { CommentQuillEditorHelper } from '../../../../clubs/helpers/comment-quill-editor.helper';
import { ClubPublicationService } from '../../../../clubs/services/publication.service';
import { EmojiHelper } from '../../../../helpers/emoji.helper';
import { QuillEditorHelper } from '../../../../helpers/quill-editor.helper';
import { UserHelper } from '../../../../helpers/user.helper';
import { Member } from '../../../../models/clubs/member.model';
import { MentionMember } from '../../../../models/clubs/mention-member.model';
import { CommentApiService } from '../../../../services/clubs/comment.api.service';
import { SignalrIntegration } from '../../../../shared/interfaces/SignalrIntegration';
import { PublicationHelper } from './../../../../helpers/publication.helper';
import { ToastrHelper } from './../../../../helpers/toastr.helper';
import { Publication } from './../../../../models/clubs/publication.model';
import { User } from './../../../../models/identity/user.model';
import { CropperParams } from './../../../../models/shared/cropper-params.model';
import { PublicationService } from './../../../../services/clubs/publication.service';
import { AccountsService } from './../../../../services/identity/accounts.service';
import { ImageService } from './../../../../services/shared/image.service';
import { PostsSignalrService } from './../../../../services/signalr/posts-signalr.service';

@Component({
    selector: 'app-add-new-comment',
    templateUrl: './add-new-comment.component.html',
    styleUrls: ['./add-new-comment.component.scss'],
})
export class AddNewCommentComponent implements OnInit, AfterViewInit, OnDestroy, SignalrIntegration {
    public _userIsWritting: boolean;
    public isMobile = false;
    public shouldDisplayButton = false;
    private destroy$: Subject<void> = new Subject<void>();
    private magicUrlEnabled = false;
    @ViewChild('emojiPicker') emojiPickerRef: ElementRef;

    public readonly PUBLICATION_CONTENT_MAX_LENGTH = 2000;
    public cropperParams: CropperParams = new CropperParams();
    public currentUser: User;
    public emojiTranslations: any;
    public isEmojiPickerVisible = false;
    public lastTimeWritting: Date;
    public localImageSrc = '';
    public isCurrentImageGif = false;
    public members: Array<Member>;
    public pos: number;

    @ViewChild('newCommentQuillEditor') public newCommentQuillEditor: QuillEditorComponent;
    @Output()
    public onCommentCreated: EventEmitter<Publication> = new EventEmitter<Publication>();
    @Input() public publication: Publication;
    @Input() public initOnCommentField = false;
    @Input() public initOnCommentFieldOwner: Member;

    public form = this.fb.group({
        content: ['', Validators.maxLength(this.PUBLICATION_CONTENT_MAX_LENGTH)],
        clubId: [null, [Validators.required]],
        userId: [null, [Validators.required]],
        parentPublicationId: [null, []],
        image: [null, []],
        picture: [null, []],
        mentionMembers: [null, []],
    });

    constructor(
        private breakpointObserver: BreakpointObserver,
        protected readonly accountService: AccountsService,
        private readonly commentApiService: CommentApiService,
        protected readonly clubsStore: ClubsStore,
        private readonly eRef: ElementRef,
        private readonly emojiHelper: EmojiHelper,
        private readonly fb: FormBuilder,
        private readonly clubPublicationService: ClubPublicationService,
        public readonly imageService: ImageService,
        protected readonly postsSignalrService: PostsSignalrService,
        private publicationHelper: PublicationHelper,
        readonly commentQuillEditorHelper: CommentQuillEditorHelper,
        public readonly quillEditorHelper: QuillEditorHelper,
        protected publicationService: PublicationService,
        private readonly toastrHelper: ToastrHelper,
        private readonly translate: TranslateService,
        private readonly userHelper: UserHelper,
    ) {
        this.postsSignalrService.restartConnectionEvent$.subscribe(() => {
            if (this.postsSignalrService.isPostFocused(this.publication.id)) {
                this.postsSignalrService.currentGroupsNames = [];
                this.initSignalr();
            }
        });
    }

    public get userIsWritting() {
        return this._userIsWritting;
    }

    public set userIsWritting(value: boolean) {
        this.postsSignalrService.onWritting(this.publication.id, value);
        this._userIsWritting = value;
    }

    public get contentLength() {
        if (!this.newCommentQuillEditor) {
            return 0;
        }
        return this.quillEditorHelper.getPlainTextLength(this.form.controls.content?.value);
    }

    public addComment() {
        if (this.publication.disabled) return;
        this.initCommonValues();
        this.form.controls.parentPublicationId.setValue(
            !this.publicationHelper.isPublicationMaxDepth(this.publication) ? this.publication.id : this.publication.parentPublicationId,
        );
        if (!this.form.valid) return;
        this.savePicture();
        this.saveMentionMembers();

        // Set content from Quill editor
        this.form.controls.content.setValue(this.newCommentQuillEditor.quillEditor.root.innerHTML);

        const command = this.form.getRawValue();

        if (this.form.controls.picture.value && this.form.controls.picture.value.size > 2 * 1024 * 1024) {
            this.toastrHelper.showError(this.translate.instant('toastMessages.pictureMaxSizeError'));
            return;
        }

        this.commentApiService.createComment(command).subscribe((result: Publication) => {
            result.hasJustBeenCreated = true;
            this.addCommentSignalr(result);

            // Reset form and editor
            this.resetForm();
            this.localImageSrc = '';
            this.publication.commentsCount++;
            this.onCommentCreated.emit(result);
            this.imageService.initCropperValues();
        });
    }

    public addEmojiToContent(data: any) {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newCommentQuillEditor);

        const emoji = data?.emoji?.native;

        if (!emoji) {
            console.error("can't find emoji");
            return;
        }

        this.newCommentQuillEditor.quillEditor.insertText(this.pos, emoji);
        this.newCommentQuillEditor.quillEditor.setSelection(this.pos + emoji.length);
    }

    public handleEmojiPickerButtonClick() {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newCommentQuillEditor);
        if (!this.publication.disabled) this.isEmojiPickerVisible = !this.isEmojiPickerVisible;
    }

    public handleRemoveImage() {
        if (!this.publication.disabled) {
            this.localImageSrc = '';
            this.isCurrentImageGif = false;
            this.form.controls.image.setValue(null);
            this.form.controls.picture.setValue(null);
        }
    }

    public onKeydown(event: KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();

            const editor = this.newCommentQuillEditor.quillEditor;

            setTimeout(() => {
                const selection = editor.getSelection();
                if (selection) {
                    editor.insertText(selection.index, '    ');
                    editor.setSelection(selection.index, 4);
                }
            }, 0);
        }
    }

    public onMentionListClosed() {
        this.clubPublicationService.setMentionOpened(false);
    }

    public onMentionListOpen() {
        this.clubPublicationService.setMentionOpened(true);
    }
    public onMentionSelected($event: MentionMember) {
        const value = this.form.controls.mentionMembers.value || [];
        value.push($event);
        this.form.controls.mentionMembers.setValue(value);
    }
    public handleDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public handleDragEnter(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const editorElement = this.newCommentQuillEditor.editorElem;
        editorElement.classList.add('drag-over');
    }

    public async handleDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        const editorElement = this.newCommentQuillEditor.editorElem;
        editorElement.classList.remove('drag-over');

        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];

            if (file.type.startsWith('image/')) {
                await this.setImageInComment(file);
            }
        }
    }

    /**
     * This method handles the paste event in the Quill editor.
     * It checks if the pasted content is an image.
     * If it is, it retrieves the image as a File and passes it to the setImageInComment method.
     *
     * @param {ClipboardEvent} event - The paste event from the Quill editor.
     */
    public async handlePaste(event: ClipboardEvent): Promise<void> {
        const result = await this.imageService.processClipboardPaste(event);
        if (result && result.imageFile) {
            await this.setImageInComment(result.imageFile);
        }
    }

    public imageHasBeenCropped(event: any) {
        this.imageService.imageCropped(event);
    }

    public initCommonValues() {
        this.form.controls.clubId.setValue(this.publication.clubId);
        this.form.controls.userId.setValue(this.currentUser.userId);
    }

    public async addCommentSignalr(comment: Publication) {
        await this.initSignalr();
        const commentToSend: Publication = comment;
        commentToSend.owner.clubs = commentToSend.owner.clubs.filter((club) => club.id == this.publication.clubId);
        if (this.userIsWritting) {
            this.userIsWritting = false;
        }
        this.postsSignalrService.addComment(this.publication.id, commentToSend);
        this.userIsWritting = false;
    }

    @HostListener('document:click', ['$event'])
    public clickout(event: MouseEvent): void {
        const clickedInside = this.eRef.nativeElement.contains(event.target) || this.emojiPickerRef?.nativeElement?.contains(event.target);

        if (!clickedInside) {
            this.isEmojiPickerVisible = false;
        }
    }

    public async initSignalr() {
        await this.postsSignalrService.joinGroup(this.publication.id, this.accountService.currentUser.userId);
    }

    public initSignalrListener() {}

    public ngOnInit(): void {
        this.currentUser = this.accountService.currentUser;
        this.cropperParams.isRatioFix = false;
        this.cropperParams.resizeToHeight = 1000;
        this.cropperParams.resizeToWidth = 1000;
        this.initCommonValues();
        this.members = this.clubsStore.flatMembers;
        this.clubsStore.membersLoadedEvent$.subscribe(() => {
            this.members = this.clubsStore.flatMembers;
        });

        this.emojiTranslations = this.emojiHelper.getTranslations();
        this.initBreakpointObserver();
        this.initFormChangeListener();
    }

    public ngAfterViewInit() {
        this.enableMagicUrl();

        if (this.initOnCommentField && this.initOnCommentFieldOwner) this.setFocusOnNewCommentTextArea(this.initOnCommentFieldOwner);
    }

    public ngOnDestroy() {
        this.disableMagicUrl();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onCancelWritting() {
        setTimeout(() => {
            const now = new Date();
            const lastTimeWritting = new Date();
            lastTimeWritting.setTime(this.lastTimeWritting.getTime());
            lastTimeWritting.setMinutes(lastTimeWritting.getMinutes() + 1);

            if (!(this.form.controls.content.value?.length > 0) || now > lastTimeWritting) {
                if (this.userIsWritting) {
                    this.userIsWritting = false;
                }
            } else {
                this.onCancelWritting();
            }
        }, 2000);
    }

    public async onWritting() {
        this.lastTimeWritting = new Date();

        if (!this.userIsWritting && this.quillEditorHelper.getPlainTextLength(this.form.controls.content.value) > 2) {
            await this.initSignalr();
            this.userIsWritting = true;
            this.onCancelWritting();
        }
    }

    public savePicture() {
        if (this.localImageSrc == '') return;

        if (this.isCurrentImageGif) {
            const originalFile = this.form.controls.picture.value;
            if (originalFile && originalFile.size > 2 * 1024 * 1024) {
                this.toastrHelper.showError(this.translate.instant('toastMessages.pictureMaxSizeError'));
                return;
            }
            return;
        }

        const format = this.imageService.getFormatFromBase64(this.imageService.croppedImage as string);

        if (this.imageService.isInBadFormat(format)) {
            this.toastrHelper.showError(this.translate.instant('toastMessages.pictureExtensionError'));
            return;
        }

        if (this.imageService.isExceedingPictureMaxSize(this.imageService.getImageSizeFromBase64(this.imageService.croppedImage as string))) {
            this.toastrHelper.showError(this.translate.instant('toastMessages.profilePictureSizeError'));
            return;
        }

        const picture = this.imageService.getImageInFile(this.form.controls.picture.value.name);
        this.form.controls.picture.setValue(picture);
        const reader = new FileReader();
        reader.readAsDataURL(picture);
        reader.onload = () => {
            this.localImageSrc = reader.result.toString();
        };
    }

    public setFocusOnNewCommentTextArea(ownerT: Member) {
        const owner = ownerT;
        const tagMember = `@${this.userHelper.getFullName(owner)} `;

        this.onMentionSelected({ tagMember, userId: owner.id });

        // Set content in Quill editor instead of form control directly
        this.newCommentQuillEditor.quillEditor.root.innerHTML = tagMember;
        this.newCommentQuillEditor.quillEditor.focus();
    }

    public async updateImage(event: any) {
        if (this.publication.disabled) {
            return;
        }
        if (event.target.files && event.target.files[0]) {
            this.setImageInComment(event.target.files[0]);
        }
    }

    public resetForm() {
        this.form.controls.content.setValue('');
        this.form.controls.image.setValue(null);
        this.form.controls.picture.setValue(null);
        this.form.controls.mentionMembers.setValue(null);

        // Clear Quill editor content
        if (this.newCommentQuillEditor) {
            this.newCommentQuillEditor.quillEditor.root.innerHTML = '';
        }
    }

    private async setImageInComment(file: File) {
        this.isCurrentImageGif = file.type === 'image/gif';
        if (await validateImage(file)) {
            this.form.controls.picture.setValue(file);
            const reader = new FileReader();
            reader.onload = (event: any) => {
                this.localImageSrc = event.target.result;
            };

            reader.readAsDataURL(file);
        } else {
            this.toastrHelper.showWarning(this.translate.instant('toastMessages.pictureExtensionError'));
        }
    }

    /**
     * Keep and save the members always tagged in the content
     */
    private saveMentionMembers() {
        let value = this.form.controls.mentionMembers.value;
        if (value) {
            const content = this.form.controls.content.value;
            value = value.filter((mention: MentionMember) => content.search(mention.tagMember) > -1);
            this.form.controls.mentionMembers.setValue(value);
        }
    }

    private initBreakpointObserver(): void {
        this.breakpointObserver
            .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.isMobile = result.matches;
                this.updateButtonDisplay();
            });
    }

    private initFormChangeListener(): void {
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateButtonDisplay();
        });
    }

    private updateButtonDisplay(): void {
        this.shouldDisplayButton = this.isMobile;
    }

    private enableMagicUrl(): void {
        if (this.newCommentQuillEditor && !this.magicUrlEnabled) {
            this.quillEditorHelper.enableMagicUrl(this.newCommentQuillEditor, {
                normalizeUrlOptions: {
                    stripWWW: false,
                    forceHttps: true,
                    removeTrailingSlash: false,
                },
            });
            this.magicUrlEnabled = true;
        }
    }

    private disableMagicUrl(): void {
        if (this.newCommentQuillEditor && this.magicUrlEnabled) {
            this.quillEditorHelper.disableMagicUrl(this.newCommentQuillEditor);
            this.magicUrlEnabled = false;
        }
    }
}

